import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaMinus, FaSearch } from "react-icons/fa";
import NavbarAdmin from "../../Components/Admin/NavbarAdmin";
import Sidebar from "../../Components/Admin/Sidebar";
import useOrders from "../../hooks/Admin/useOrders";
import useFetchProducts from "../../hooks/Admin/useProduct";
import useUsers from "../../hooks/Admin/useGetUsers";
import logo from "../../Assets/logo.png";

// Function to parse address from a combined string (for buyer's address)
const parseAddress = (inputString) => {
  if (!inputString || typeof inputString !== "string") {
    return "";
  }
  const parts = inputString.split(",").map((part) => part.trim());
  
  console.log("Parsing buyer's order.address:", inputString);

  // Address starts from the third field (index 2: e.g., "sad123, Poblacion, Sierra Bullones")
  const addressParts = parts.slice(2).map((part) => part.trim());
  const firstAddressPart = addressParts[0] || "";
  const addressPrefix = firstAddressPart;
  const remainingAddress = addressParts.slice(1).join(", ").trim();
  return remainingAddress ? `${addressPrefix}, ${remainingAddress}` : addressPrefix;
};

// Function to parse contact from order.address (buyer's contact)
const parseContact = (inputString) => {
  if (!inputString || typeof inputString !== "string") {
    return "";
  }
  const parts = inputString.split(",").map((part) => part.trim());
  
  console.log("Parsing buyer's contact from order.address:", inputString);

  let contact = parts[1] || "";
  
  const phoneNumberPattern = /^\+?\d+[-]?\d*[-]?\d*$/;
  if (!phoneNumberPattern.test(contact)) {
    contact = "";
  }

  return contact;
};

const Orders = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { orders: initialOrders, isLoading, error, updateOrder, updateStatus, updateError } = useOrders();
  const { products, loading: loadingProducts, error: errorProducts } = useFetchProducts();
  const { users, loading: loadingUsers, error: errorUsers } = useUsers();

  // Sync initial orders from useOrders when it loads
  useEffect(() => {
    if (initialOrders && !isLoading && !error) {
      setOrders(initialOrders);
    }
  }, [initialOrders, isLoading, error]);

  const totalOrders = orders.length;

  const enrichedOrders = orders.map((order) => {
    const product = products.find((prod) => prod.id === order.product_id) || {};
    const user = users.find((u) => u.id === order.user_id) || {};
    const total = (order.quantity * order.price).toFixed(2);
    const unit = product.is_kilo ? "per kilo" : product.is_pieces ? "per piece/s" : "";

    // Parse the buyer's address and contact from order.address
    const parsedAddress = parseAddress(order.address);
    const buyerContact = parseContact(order.address);

    return {
      ...order,
      product_id: product.id || "Unknown ID",
      product_name: product.product_name || "Unknown Product",
      fullname: user.fullname || "Unknown User",
      email: user.email || "Unknown Email",
      contact: buyerContact,
      address: parsedAddress, // Ensure address is always parsed
      rawAddress: order.address, // Store the raw address for updates
      total,
      unit,
    };
  });

  const filteredOrders = enrichedOrders.filter((order) => {
    const lowerSearch = searchTerm.trim().toLowerCase();
    return (
      order.product_name?.toLowerCase().includes(lowerSearch) ||
      order.fullname?.toLowerCase().includes(lowerSearch) ||
      order.email?.toLowerCase().includes(lowerSearch) ||
      order.status?.toLowerCase().includes(lowerSearch)
    );
  });

  const displayedOrders = showAll ? filteredOrders : filteredOrders.slice(0, 10);

  const handleStatusChange = (e) => {
    if (selectedOrder) {
      setSelectedOrder({ ...selectedOrder, status: e.target.value });
    }
  };

  const handleSave = async () => {
    if (selectedOrder) {
      const orderData = {
        id: selectedOrder.id,
        product_id: selectedOrder.product_id,
        user_id: selectedOrder.user_id,
        quantity: selectedOrder.quantity,
        price: selectedOrder.price,
        address: selectedOrder.rawAddress, // Use the raw address for the update
        status: selectedOrder.status,
      };

      await updateOrder(orderData);

      if (updateStatus === "updated_successfully") {
        const updatedOrders = orders.map((order) =>
          order.id === selectedOrder.id ? { ...order, status: selectedOrder.status } : order
        );
        setOrders(updatedOrders);
        setSelectedOrder(null);
      } else if (updateError) {
        console.error("Failed to update order:", updateError);
      }
    }
  };

  const isLoadingCombined = isLoading || loadingProducts || loadingUsers;
  const hasError = error || errorProducts || errorUsers;

  return (
    <PageContainer>
      <NavbarAdmin setIsSidebarOpen={setIsSidebarOpen} />
      <Container>
        <Sidebar isOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <Content $isSidebarOpen={isSidebarOpen}>
          <TitleContainer>
            <Title>ORDERS</Title>
            <SearchBox>
              <input
                type="text"
                placeholder="Search product, name, email, or status"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="searchIcon" />
            </SearchBox>
            <SummaryCard>
              <p>Total:</p>
              <h2>{totalOrders}</h2>
            </SummaryCard>
          </TitleContainer>

          {isLoadingCombined ? (
            <LoadingOverlay>
              <SpinnerContainer>
                <LogoSpinnerContainer>
                  <LoadingLogo src={logo} alt="Loading Logo" />
                  <InnerSpinner />
                </LogoSpinnerContainer>
                <LoadingText>LOADING</LoadingText>
              </SpinnerContainer>
            </LoadingOverlay>
          ) : hasError ? (
            <ErrorText>Error: {hasError}</ErrorText>
          ) : (
            <TableAndDetails>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      {[
                        "No.",
                        "Product Name",
                        "Quantity",
                        "Price",
                        "Total",
                        "Fullname",
                        "Address",
                        "Contact",
                        "Status",
                        "Date",
                      ].map((heading, index) => (
                        <th key={index}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedOrders.map((order, index) => (
                      <tr
                        key={index}
                        onClick={() => setSelectedOrder(order)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{index + 1}</td>
                        <td>{order.product_name}</td>
                        <td>{order.quantity}</td>
                        <td>
                          ₱{parseFloat(order.price).toFixed(2)} {order.unit}
                        </td>
                        <td>₱{order.total}</td>
                        <td>{order.fullname}</td>
                        <td>{order.address || ""}</td> 
                        <td>{order.contact}</td>
                        <td>{order.status}</td>
                        <td>{order.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {filteredOrders.length > 10 && (
                  <ShowAllButton onClick={() => setShowAll(!showAll)}>
                    {showAll ? "Show Less" : "Show All Orders"}
                  </ShowAllButton>
                )}
              </TableContainer>

              {selectedOrder && !isMinimized && (
                <DetailContainer>
                  <MinimizeButton onClick={() => setSelectedOrder(null)}>
                    <FaMinus />
                  </MinimizeButton>
                  <h3>Order Details</h3>
                  <DetailItem>
                    <label>Product:</label>
                    <input value={selectedOrder.product_name || ""} readOnly />
                  </DetailItem>
                  <DetailItem>
                    <label>Quantity:</label>
                    <input value={selectedOrder.quantity || ""} readOnly />
                  </DetailItem>
                  <DetailItem>
                    <label>Price:</label>
                    <input
                      value={
                        selectedOrder.price
                          ? `₱${parseFloat(selectedOrder.price).toFixed(2)} ${selectedOrder.unit}`
                          : ""
                      }
                      readOnly
                    />
                  </DetailItem>
                  <DetailItem>
                    <label>Total:</label>
                    <input
                      value={selectedOrder.total ? `₱${selectedOrder.total}` : ""}
                      readOnly
                    />
                  </DetailItem>
                  <DetailItem>
                    <label>Customer:</label>
                    <input value={selectedOrder.fullname || ""} readOnly />
                  </DetailItem>
                  <DetailItem>
                    <label>Address:</label>
                    <input value={selectedOrder.address || "N/A"} readOnly /> {/* Ensure address is always displayed */}
                  </DetailItem>
                  <DetailItem>
                    <label>Contact:</label>
                    <input value={selectedOrder.contact || ""} readOnly />
                  </DetailItem>
                  <DetailItem>
                    <label>Status:</label>
                    <select
                      value={selectedOrder.status || ""}
                      onChange={handleStatusChange}
                    >
                      <option value="to pay">to pay</option>
                      <option value="to receive">to receive</option>
                      <option value="to rate">to rate</option>
                      <option value="completed">completed</option>
                      <option value="return">return</option>
                    </select>
                  </DetailItem>
                  <SaveButton onClick={handleSave}>SAVE</SaveButton>
                </DetailContainer>
              )}
            </TableAndDetails>
          )}
        </Content>
      </Container>
    </PageContainer>
  );
};

export default Orders;

// Styled Components

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: #f9f9f9;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  background: #eaf4e1;
`;

const Content = styled.div`
  padding: 20px;
  width: ${({ $isSidebarOpen }) =>
    $isSidebarOpen ? "calc(100% - 240px)" : "calc(100% - 100px)"};
  margin-left: ${({ $isSidebarOpen }) => ($isSidebarOpen ? "200px" : "60px")};
  transition: all 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const TableAndDetails = styled.div`
  display: flex;
  gap: 20px;
`;

const DetailContainer = styled.div`
  min-width: 300px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  height: fit-content;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: 20px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0;

  label {
    font-weight: bold;
    margin-bottom: 5px;
  }

  input {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 20px;
    outline: none;
    font-size: 14px;
  }

  select {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 20px;
    outline: none;
    font-size: 14px;
    appearance: none;
    background: white;
  }
`;

const SaveButton = styled.button`
  background-color: #21551c;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  margin: 0 auto; /* Center the button horizontally */
  width: fit-content;

  &:hover {
    background-color: #1a4316;
  }
`;

const MinimizeButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #21551c;

  &:hover {
    color: darkgreen;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding-bottom: 10px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #21551c;
`;

const SummaryCard = styled.div`
  background: #d4edda;
  padding: 10px 10px;
  border-radius: 5px;
  border: 1px solid #21551c;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  width: 80px;
  height: 20px;
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
  flex: 2;
  min-width: 300px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 10px;
    border-bottom: 1px solid #ccc;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    background-color: #f4f4f4;
    color: #21551c;
  }
`;

const ErrorText = styled.p`
  color: red;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoSpinnerContainer = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
`;

const LoadingLogo = styled.img`
  width: 50px;
  height: 50px;
  z-index: 1;
  border-radius: 50%;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const InnerSpinner = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.5);
  border-top: 3px solid #004d00;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  pointer-events: none;
`;

const LoadingText = styled.span`
  color: #004d00;
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 10px;

  &::after {
    content: ".";
    animation: dots 1.5s steps(3, end) infinite;
    margin-left: 5px;
  }

  @keyframes dots {
    0% { content: "."; }
    33% { content: ".."; }
    66% { content: "..."; }
    100% { content: "."; }
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: white;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #21551c;
  width: 300px;
  margin-left: auto;

  input {
    border: none;
    flex: 1;
    padding: 8px;
    outline: none;
    width: 100%;
  }

  .searchIcon {
    color: #21551c;
    margin-right: 5px;
  }
`;

const ShowAllButton = styled.button`
  margin: 10px auto; /* Center horizontally with top margin */
  display: block; /* Ensure it behaves as a block element for centering */
  background: none;
  border: none;
  color: #21551c;
  font-weight: bold;
  cursor: pointer;
  font-size: 16px;
  font-family: 'Poppins', sans-serif; /* Ensure button uses Poppins */

  &:hover {
    text-decoration: underline;
  }
`;